import { useTranslation } from 'react-i18next';
import Badge from './Badge';

export type ZKAnchorStatus = 'pending' | 'confirmed' | 'revoked' | 'failed';

interface Props {
  status: ZKAnchorStatus;
  blockNumber?: number | null;
  revokedBlock?: number | null;
  /** Tooltip text override. */
  title?: string;
  /** Compact pill (no block number suffix). */
  compact?: boolean;
}

const variantFor = (s: ZKAnchorStatus) => {
  switch (s) {
    case 'confirmed': return 'success' as const;
    case 'revoked':   return 'error'   as const;
    case 'failed':    return 'error'   as const;
    case 'pending':
    default:          return 'neutral' as const;
  }
};

/**
 * Status pill for an on-chain ZK anchor.
 *
 * Colours follow the architecture spec (§4.8):
 * - pending   slate
 * - confirmed emerald
 * - revoked   red
 *
 * Falls back to `compact` form when only the verb matters.
 */
export default function ZKAnchorBadge({ status, blockNumber, revokedBlock, title, compact }: Props) {
  const { t } = useTranslation('zk');
  const variant = variantFor(status);

  const label = (() => {
    if (compact) return t(`anchor.status.${status}`);
    if (status === 'revoked' && revokedBlock != null) {
      return t('anchor.statusWithBlock.revoked', { block: revokedBlock });
    }
    if (status === 'confirmed' && blockNumber != null) {
      return t('anchor.statusWithBlock.confirmed', { block: blockNumber });
    }
    return t(`anchor.status.${status}`);
  })();

  const tooltip = title ?? (status === 'revoked' && revokedBlock != null
    ? t('anchor.tooltip.revoked', { block: revokedBlock })
    : status === 'pending'
      ? t('anchor.tooltip.pending')
      : status === 'failed'
        ? t('anchor.tooltip.failed')
        : t('anchor.tooltip.confirmed', { block: blockNumber ?? '?' }));

  return (
    <span title={tooltip}>
      <Badge variant={variant} pulse={status === 'pending'}>
        {label}
      </Badge>
    </span>
  );
}
